"use client";

import { JSX } from "react";
import { cn } from "@/lib/utils";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  MiniAreaChart,
  MiniBarChart,
  MiniSparkline,
} from "@/components/charts/mini";
import type { ModuleSocialProofStatsData } from "@/types/modules";
import { renderInlineMarkdown, stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";

export default function ModuleSocialProofStats({
  data,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleSocialProofStatsData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const stats = data?.stats ?? [];
  return (
    <SwipeScroll
      className={cn(
        "w-full [mask-image:linear-gradient(to_right,transparent,black_2%,black_98%,transparent)]",
        className,
      )}
    >
      <div
        className="flex w-max flex-row gap-4 pr-10"
        style={importsTokenScope(grayscale, accent)}
      >
        {stats.map((stat) => (
          <div
            key={stat.id}
            className="relative w-[260px] shrink-0 rounded-xl p-5 ring-1 ring-inset ring-black/[0.08] dark:ring-white/[0.10]"
          >
            <div className={`mb-3 text-xs text-[var(--muted)] ${MODULE_LABEL_WRAP.cell}`}>
              {stripInlineMarkdown(stat.label)}
            </div>
            <div
              className="mb-2 text-[40px] leading-none tracking-tight text-[var(--fg)] font-medium"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {stat.value}
            </div>
            <div className={`mb-5 text-xs text-[var(--muted)] ${MODULE_LABEL_WRAP.cell}`}>
              {renderInlineMarkdown(stat.caption)}
            </div>

            {stat.visual.kind === "avatars" && (
              <div className="-space-x-2 flex items-center">
                {stat.visual.people.map((p, i) => (
                  <div
                    key={i}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium"
                    style={{
                      background: `hsl(0, 0%, ${p.shade ?? 20}%)`,
                      color: "#ffffff",
                      boxShadow: "0 0 0 2px var(--card)",
                    }}
                  >
                    {p.initials}
                  </div>
                ))}
                {stat.visual.extraCount !== undefined && (
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-full text-xs text-[var(--subtle)]"
                    style={{
                      background: "var(--bg)",
                      boxShadow: "0 0 0 2px var(--card)",
                    }}
                  >
                    +{stat.visual.extraCount}
                  </div>
                )}
              </div>
            )}

            {stat.visual.kind === "bars" && (
              <div className="h-10">
                <MiniBarChart data={stat.visual.data} />
              </div>
            )}

            {stat.visual.kind === "spark" && (
              <div className="h-10">
                <MiniSparkline data={stat.visual.data} />
              </div>
            )}

            {stat.visual.kind === "area" && (
              <div className="h-10">
                <MiniAreaChart data={stat.visual.data} />
              </div>
            )}
          </div>
        ))}
      </div>
    </SwipeScroll>
  );
}
